import { useEffect, useMemo, useState } from "react";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import NumberInput from "@shared/v2/NumberInput";
import TextButton from "@shared/v2/TextButton";
import TextLink from "@shared/v2/TextLink";
import Typography from "@shared/v2/Typography";
import Tooltip from "@shared/v2/Tooltip";
import { ArrowRightSolidV6, ChartMixedSolidV6, CircleInfoSolidV6 } from "@shared/v2/Icomoon";
import SaveProgressButton from "./SaveProgressButton";
import { useWizardContext } from "./WizardContext";
import { useCurrentUser } from "../../../reducers/layoutReducer/selectors";

const PRODUCTIVITY_FIELDS = [
  { key: "callsLastTwelveMonths", label: "Calls in Brivity" },
  {
    key: "contactsLastTwelveMonths",
    label: "Contacts",
    tooltip: 'Calls made with an outcome of "Talked".',
  },
  { key: "appointmentsSetLastTwelveMonths", label: "Appointments Set" },
  { key: "appointmentsHeldLastTwelveMonths", label: "Appointments Held" },
  { key: "signingsLastTwelveMonths", label: "Signings" },
  { key: "unitsPendedLastTwelveMonths", label: "Pended Units" },
  { key: "unitsClosedLastTwelveMonths", label: "Units Closed" },
];

const createTeamDefaultStatsFromGoals = (goals = {}) =>
  Object.fromEntries(PRODUCTIVITY_FIELDS.map(({ key }) => [key, String(goals[key] ?? 0)]));

const hasAnyNonZeroStat = (stats = {}) =>
  PRODUCTIVITY_FIELDS.some(({ key }) => Number(stats?.[key] ?? 0) !== 0);

const hasAnyZeroStat = (stats = {}) => PRODUCTIVITY_FIELDS.some(({ key }) => Number(stats?.[key] ?? 0) === 0);

const createStatsFromGoals = (goals = {}) => {
  const savedPersonalStats = goals.personalProductivityStats || {};
  const personalStats = hasAnyNonZeroStat(savedPersonalStats) ? savedPersonalStats : {};
  const salesFunnelStats = goals.agentSalesFunnelStats || {};

  return Object.fromEntries(
    PRODUCTIVITY_FIELDS.map(({ key }) => [key, String(personalStats[key] ?? salesFunnelStats[key] ?? "0")]),
  );
};

const createSalesFunnelStatsFromGoals = (goals = {}) => {
  const salesFunnelStats = goals.agentSalesFunnelStats || {};
  return Object.fromEntries(
    PRODUCTIVITY_FIELDS.map(({ key }) => [key, String(salesFunnelStats[key] ?? "0")]),
  );
};

const areStatsEqual = (a = {}, b = {}) =>
  PRODUCTIVITY_FIELDS.every(({ key }) => String(a[key] ?? "0") === String(b[key] ?? "0"));

const normalizeStatValue = (value) => {
  const cleanValue = String(value ?? "").replace(/[^0-9]/g, "");
  return cleanValue.length ? cleanValue : "0";
};

const ProductivityStats = () => {
  const currentUser = useCurrentUser();
  const {
    goToPage,
    year,
    saveProgress,
    wizardData: { goals, actions },
  } = useWizardContext();

  const [productivityStats, setProductivityStats] = useState(() => createStatsFromGoals(goals));
  const initialProductivityStats = useMemo(() => createStatsFromGoals(goals), [goals]);
  const teamDefaultStats = useMemo(() => createTeamDefaultStatsFromGoals(goals), [goals]);
  const salesFunnelStats = useMemo(() => createSalesFunnelStatsFromGoals(goals), [goals]);

  useEffect(() => {
    setProductivityStats(initialProductivityStats);
  }, [initialProductivityStats]);

  const resetDisabled = useMemo(
    () => areStatsEqual(productivityStats, salesFunnelStats),
    [productivityStats, salesFunnelStats],
  );

  const hasZeroProductivityStat = hasAnyZeroStat(productivityStats);

  const onStatChange = (key) => (e) => {
    setProductivityStats((prev) => ({
      ...prev,
      [key]: normalizeStatValue(e.target.value),
    }));
  };

  const persistStats = ({ nextPage } = {}) => {
    const goalsToSave = {
      ...goals,
      personalProductivityStats: productivityStats,
    };
    actions?.setLocalGoals(goalsToSave);
    saveProgress({ nextPage, nextGoals: goalsToSave });
  };

  return (
    <>
      <Modal.Header
        title={
          <>
            {`${year} Business Planning`} <span className="tw-text-12d tw-text-gray-50">Step 3 of 5</span>
          </>
        }
        closeButton
      />

      <Modal.Body className="tw-flex tw-flex-col tw-gap-[16px] tw-overflow-y-auto">
        <div className="tw-flex tw-flex-col tw-gap-[4px]">
          <Typography variant="subtitle-m-bold">
            What were your productivity stats from the last 12 months?
          </Typography>
          <Typography variant="body-s" className="!tw-text-gray-50 tw-mb-[4px]">
            This will help us determine your conversion rates so we can tailor your Activity Goals towards
            your business.
          </Typography>
          <Alert
            variant={currentUser.account.isPlace || currentUser.account.isLever ? "info" : "brivity"}
            icon={<ChartMixedSolidV6 size="l" />}
            text={
              <div className="tw-text-12d">
                If you have stats in your{" "}
                <TextLink
                  className="!tw-text-inherit !tw-underline tw-decoration-theme-brand-light"
                  href="/agent_stats?report_tab=agentDashboard"
                  target="_blank"
                >
                  sales funnel
                </TextLink>{" "}
                from the last 12 months, they will automatically appear here. If you are a new agent or don’t
                have complete data, click Use Team Defaults.
              </div>
            }
            customAction={
              <Button schema="secondary" onClick={() => setProductivityStats(teamDefaultStats)}>
                Use Team Defaults
              </Button>
            }
          />
        </div>
        <table>
          <thead>
            <tr className="[&>th]:tw-py-[8px] tw-text-12d tw-text-gray-50 tw-whitespace-nowrap tw-border-solid tw-border-[1px] tw-border-gray-30 tw-border-x-0">
              <th className="tw-pr-[4px] tw-font-semibold">Production Stat</th>
              <th className="tw-pl-[4px] tw-font-semibold tw-text-right"># Completed in Last 12 Months</th>
            </tr>
          </thead>
          <tbody className="[&>tr:nth-child(even)]:tw-bg-gray-5">
            {PRODUCTIVITY_FIELDS.map(({ key, label, tooltip }) => (
              <tr key={key} className="[&>td]:tw-py-[4px] [&:first-child>td]:tw-pt-[8px]">
                <td className="tw-pr-[4px] tw-text-12d tw-text-gray-50 tw-font-semibold" valign="middle">
                  {label}{" "}
                  {tooltip && (
                    <Tooltip
                      trigger={<CircleInfoSolidV6 />}
                      content={tooltip}
                      innerClassName="tw-max-w-[180px]"
                      multiline
                    />
                  )}
                </td>
                <td className="tw-pl-[4px]" align="right" valign="middle">
                  <div className="tw-inline-flex tw-max-w-[100px]">
                    <NumberInput
                      inputProps={{
                        className: "tw-min-w-[100px]",
                      }}
                      value={Number(productivityStats[key]) || 0}
                      error={hasZeroProductivityStat && Number(productivityStats[key]) === 0}
                      onChange={onStatChange(key)}
                      showControls={false}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px]">
        <Button schema="tertiary" size="medium" onClick={() => goToPage(4)}>
          Back
        </Button>
        <div className="tw-flex tw-gap-[12px]">
          <TextButton onClick={() => setProductivityStats(salesFunnelStats)} disabled={resetDisabled}>
            Reset Values
          </TextButton>
          <SaveProgressButton onClick={() => persistStats()} />
          <Button
            size="medium"
            className="tw-inline-flex tw-items-center tw-gap-[6px]"
            onClick={() => persistStats({ nextPage: 6 })}
            disabled={hasZeroProductivityStat}
          >
            Next <ArrowRightSolidV6 />
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

export default ProductivityStats;
